import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { TextField } from './inputs/TextField';
import type { PermissionCatalogItem, PermissionCatalogModule } from '../features/administration/roles/roleTypes';
import { Checkbox } from './inputs/Checkbox';

interface PermissionPickerProps {
  /** The full server-side catalog, grouped by module. */
  catalog: PermissionCatalogModule[];
  /** Checked permission keys. */
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * Restricts what may be offered at all. Anything outside it is dropped before rendering,
   * so a surface bounded by a role cannot show — let alone tick — a key the role never
   * grants. Omit to offer the whole catalog.
   */
  limitToKeys?: string[];
  disabled?: boolean;
  /** Prefix for every generated DOM id. Must be unique on the page. */
  idPrefix: string;
  /** Shown above the search box when there is something worth saying. */
  description?: string;
  /** Shown when the bounded catalog is empty — a different case from a search miss. */
  emptyMessage?: string;
  /**
   * Opens showing only the modules this account actually holds something in, with a control
   * to reveal the rest.
   *
   * For a READ-ONLY view of what an account has. A profile answers "what can this person
   * do", and the whole catalog answers it by burying eighteen enabled rows in two hundred
   * empty ones — the modules the account holds nothing in are the ones it is not about.
   *
   * Never on an editing surface. Granting a permission means finding a module that is
   * currently empty, so hiding the empty ones would hide everything left to grant.
   */
  showOnlyEnabled?: boolean;
}

/**
 * Searchable permission picker — the one place permissions are chosen.
 *
 * Both surfaces that grant permissions use it: the role editor over the whole catalog, and
 * the create-user form bounded to a single role via `limitToKeys`.
 *
 * **One table per module.** A single table with module rows threaded through it made the
 * header stop applying halfway down and left no obvious place for a module's own
 * description or its bulk toggle. Each module is its own bordered table with its own header,
 * so the columns always mean what the header says and a module can be read on its own.
 * Modules are grouped under their suite.
 */
export function PermissionPicker({
  catalog,
  value,
  onChange,
  limitToKeys,
  disabled = false,
  idPrefix,
  description,
  emptyMessage = 'This role grants no permissions yet.',
  showOnlyEnabled = false,
}: PermissionPickerProps) {
  const [query, setQuery] = useState('');
  const [revealAll, setRevealAll] = useState(false);

  const allowed = useMemo(() => (limitToKeys ? new Set(limitToKeys) : null), [limitToKeys]);
  const checked = useMemo(() => new Set(value), [value]);

  // The bounded catalog: what this surface may offer at all, before any search.
  const scoped = useMemo(() => {
    if (!allowed) return catalog;

    return catalog
      .map((module) => ({
        ...module,
        permissions: module.permissions.filter((p) => allowed.has(p.key)),
      }))
      .filter((module) => module.permissions.length > 0);
  }, [catalog, allowed]);

  const offered = useMemo(
    () => scoped.flatMap((module) => module.permissions.map((p) => p.key)),
    [scoped],
  );

  /*
    The modules this account actually holds something in.

    A whole module is kept or dropped — never individual rows. A module the account holds one
    of four permissions in still shows all four, because "what can this person do here" is
    answered by the three that are off as much as by the one that is on. It is the modules
    holding nothing that say nothing.
  */
  const inScope = useMemo(() => {
    if (!showOnlyEnabled || revealAll) return scoped;

    return scoped.filter((module) => module.permissions.some((p) => checked.has(p.key)));
  }, [scoped, showOnlyEnabled, revealAll, checked]);

  const hiddenModuleCount = scoped.length - inScope.length;

  const term = query.trim().toLowerCase();

  const visible = useMemo(() => {
    if (!term) return inScope;

    const matches = (p: PermissionCatalogItem, module: PermissionCatalogModule) =>
      p.name.toLowerCase().includes(term) ||
      p.key.toLowerCase().includes(term) ||
      (p.description?.toLowerCase().includes(term) ?? false) ||
      module.name.toLowerCase().includes(term) ||
      (module.description?.toLowerCase().includes(term) ?? false);

    return inScope
      .map((module) => ({
        ...module,
        permissions: module.permissions.filter((p) => matches(p, module)),
      }))
      .filter((module) => module.permissions.length > 0);
  }, [inScope, term]);

  const bySuite = useMemo(() => {
    const groups = new Map<string, PermissionCatalogModule[]>();

    for (const module of visible) {
      groups.set(module.suite, [...(groups.get(module.suite) ?? []), module]);
    }

    return [...groups.entries()];
  }, [visible]);

  function setKeys(keys: string[], on: boolean) {
    const next = new Set(checked);

    for (const key of keys) {
      if (on) next.add(key);
      else next.delete(key);
    }

    onChange([...next]);
  }

  const chosenCount = offered.filter((key) => checked.has(key)).length;
  const matchCount = visible.reduce((total, module) => total + module.permissions.length, 0);

  if (scoped.length === 0) {
    return <EmptyState message={emptyMessage} minHeight="min-h-[160px]" />;
  }

  return (
    <div className="flex flex-col gap-4">
      {description ? <p className="text-xs text-slate-500">{description}</p> : null}

      {/*
        Never disabled by `disabled`.

        `disabled` means the grants cannot be CHANGED — it belongs on the checkboxes and
        nowhere else. Search is how the list is read, and a read-only picker is still a thing
        someone reads: on a profile showing sixty permissions it is the only way to find one.
        Disabling it turned "you may not edit this" into "you may not look through this".
      */}
      <TextField
        id={`${idPrefix}-search`}
        label="Search permissions"
        type="search"
        placeholder="Filter by function, key, or description…"
        leading={<Search className="h-4 w-4" aria-hidden="true" />}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {/*
        The count is of the WHOLE bounded catalog, not of what is on screen — it is the
        headline fact about the account, and it must not change when a module is hidden.
      */}
      <div className="flex min-w-0 items-center gap-3">
        <p className="min-w-0 flex-1 text-xs text-slate-500" aria-live="polite">
          {chosenCount} of {offered.length} enabled
          {term ? ` · ${matchCount} shown` : null}
        </p>

        {/*
          Reveal is offered only when something is actually hidden, and the way back is
          offered only once it has been taken — a control that would change nothing does not
          render. The label counts what it will show, so the reader knows what they are
          asking for before they ask.
        */}
        {showOnlyEnabled && (hiddenModuleCount > 0 || revealAll) ? (
          <Button
            id={`${idPrefix}-reveal-all`}
            variant="tertiary"
            className="shrink-0 px-2 py-1 text-xs"
            onClick={() => setRevealAll((shown) => !shown)}
          >
            {revealAll
              ? 'Show only enabled'
              : `Show all ${scoped.length} module${scoped.length === 1 ? '' : 's'}`}
          </Button>
        ) : null}
      </div>

      {bySuite.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
          {term
            ? 'No permission matches that search.'
            : /*
                Not a search miss: the account holds nothing anywhere. Said plainly, because
                an empty panel under a "0 of 42 enabled" line reads as a failed render.
              */
              'This account holds no permissions.'}
        </p>
      ) : (
        bySuite.map(([suite, modules]) => (
          <section key={suite} className="flex flex-col gap-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {suite}
            </h3>

            {modules.map((module) => (
              <ModuleTable
                key={module.key}
                module={module}
                checked={checked}
                disabled={disabled}
                idPrefix={idPrefix}
                onSetKeys={setKeys}
              />
            ))}
          </section>
        ))
      )}
    </div>
  );
}

/** One module, one table. Its header carries the module's own name, purpose, and bulk toggle. */
function ModuleTable({
  module,
  checked,
  disabled,
  idPrefix,
  onSetKeys,
}: {
  module: PermissionCatalogModule;
  checked: Set<string>;
  disabled: boolean;
  idPrefix: string;
  onSetKeys: (keys: string[], on: boolean) => void;
}) {
  const keys = module.permissions.map((p) => p.key);
  const chosen = keys.filter((k) => checked.has(k));
  const allOn = chosen.length === keys.length;

  return (
    <div
      id={`${idPrefix}-module-${module.key}-table`}
      className="overflow-hidden rounded-lg border border-slate-200"
    >
      {/*
        The module's name and purpose only. Its "All" toggle used to sit here as well, and is
        now the select-all at the head of the checkbox column — one control, in the standard
        place, rather than a second one a row above it.
      */}
      <header className="min-w-0 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {module.name}
        </p>
        {module.description ? (
          <p className="mt-0.5 text-xs text-slate-500">{module.description}</p>
        ) : null}
      </header>

      {/* Dense content stays inside its own scroller so the page never scrolls sideways. */}
      <div className="modern-scrollbar overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200">
              {/*
                The checkbox leads, as it does on every other table in the product — the
                standard gutter, ahead of the data columns. It used to sit last under an
                ENABLED heading, which put the one control on the row at the end of it and
                made this the only table where the ticks did not line up under the reader's
                eye on the way down.

                The header cell is the module's select-all, which is where a select-all
                belongs: at the head of the column it controls.
              */}
              <th scope="col" className="w-10 px-3 py-2">
                <Checkbox
                  id={`${idPrefix}-module-${module.key}`}
                  name={`${idPrefix}-module-${module.key}`}
                  checked={allOn}
                  indeterminate={chosen.length > 0 && !allOn}
                  onChange={() => onSetKeys(keys, !allOn)}
                  disabled={disabled}
                  aria-label={`Enable every ${module.name} permission`}
                />
              </th>
              <th
                scope="col"
                className="w-[34%] px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
              >
                Function
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
              >
                Description
              </th>
            </tr>
          </thead>

          <tbody>
            {module.permissions.map((permission) => (
              <tr
                key={permission.key}
                className="border-b border-slate-100 transition last:border-b-0 hover:bg-slate-50"
              >
                <td className="px-3 py-2 align-top">
                  <Checkbox
                    id={`${idPrefix}-${permission.key}`}
                    name={`${idPrefix}-${permission.key}`}
                    checked={checked.has(permission.key)}
                    onChange={(e) => onSetKeys([permission.key], e.target.checked)}
                    disabled={disabled}
                  />
                </td>

                <td className="px-3 py-2 align-top">
                  <label
                    htmlFor={`${idPrefix}-${permission.key}`}
                    className="block cursor-pointer text-sm font-medium text-slate-800"
                  >
                    {permission.name}
                  </label>
                  {/* The key is a machine detail beside the human name: grey, not a link. */}
                  <span className="mt-0.5 block font-mono text-xs text-slate-500">
                    {permission.key}
                  </span>
                </td>

                <td className="px-3 py-2 align-top text-sm text-slate-600">
                  {permission.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
